/**
 * Admin-curated bundle products controller
 * MIT Wellness Theme
 */
(() => {
  const pluralRules = new Map();
  const pluralCategory = (locale, count) => {
    try {
      if (!pluralRules.has(locale)) pluralRules.set(locale, new Intl.PluralRules(locale));
      return pluralRules.get(locale).select(count);
    } catch (_) { return count === 1 ? 'one' : 'other'; }
  };

  const registerBundleProducts = () => {
    if (!window.Alpine || window.Alpine._bundleProductsRegistered) return;

    window.Alpine._bundleProductsRegistered = true;
    Alpine.data('xBundleProducts', (config = {}) => ({
      sectionId: config.sectionId || '',
      discountPercentage: Number(config.discountPercentage || 0),
      moneyFormat: config.moneyFormat || '${{amount}}',
      bundleTitle: config.bundleTitle || '',
      messages: config.messages || {},
      locale: config.locale || 'en',
      ctaLabel: config.ctaLabel || '',
      subscriptionCtaLabel: config.subscriptionCtaLabel || '',
      discountLabel: config.discountLabel || '',
      products: [],
      loading: false,
      errorMessage: '',
      cartRecovery: false,
      cartRefreshFailed: false,
      expandedProductIndex: null,
      selectedScheduleKey: '',
      purchaseType: 'one-time',
      purchaseOptions: false,
      pendingPurchase: '',
      statusMessage: '',

      init() {
        if (typeof config === 'string') {
          this.sectionId = config;
        }

        const configEl = document.getElementById(`bp-config-${this.sectionId}`);
        if (configEl) {
          try {
            const parsedConfig = JSON.parse(configEl.textContent || '{}');
            this.discountPercentage = Number(parsedConfig.discountPercentage || this.discountPercentage);
            this.moneyFormat = parsedConfig.moneyFormat || this.moneyFormat;
            this.bundleTitle = parsedConfig.bundleTitle || this.bundleTitle;
            this.discountLabel = parsedConfig.discountLabel || this.discountLabel;
            this.purchaseOptions = !!parsedConfig.purchaseOptions;
            this.messages = parsedConfig.messages || this.messages;
            this.locale = parsedConfig.locale || this.locale;
            this.ctaLabel = parsedConfig.ctaLabel || this.ctaLabel;
            this.subscriptionCtaLabel = parsedConfig.subscriptionCtaLabel || this.subscriptionCtaLabel;
          } catch (e) {
            console.error('Failed to parse bundle config data:', e);
          }
        }

        const dataEl = document.getElementById(`bp-data-${this.sectionId}`);
        if (dataEl) {
          try {
            this.products = JSON.parse(dataEl.textContent || '[]').filter(Boolean);
          } catch (e) {
            console.error('Failed to parse bundle products data:', e);
            this.products = [];
          }
        }
        // Use the same merchant ordering as the initial Liquid render.
        this.selectedScheduleKey = this.subscriptionSchedules[0]?.key || '';
        if (this.purchaseOptions && this.requiresSubscription) this.purchaseType = 'subscription';
        this.$root?.querySelectorAll('[data-bp-initial]').forEach(element => element.remove());
      },

      message(key, variables = {}) {
        return String(this.messages[key] || '').replace(/__(\w+)__/g, (token, name) =>
          Object.hasOwn(variables, name) ? String(variables[name]) : token);
      },

      countMessage(key, count, variables = {}) {
        const category = pluralCategory(this.locale, count);
        const messageKey = key + category[0].toUpperCase() + category.slice(1);
        return this.message(Object.hasOwn(this.messages, messageKey) ? messageKey : key + 'Other', {
          ...variables, item_count: count, order_count: count
        });
      },

      get purchaseCtaLabel() {
        return this.purchaseType === 'subscription' && this.subscriptionCtaLabel ? this.subscriptionCtaLabel : this.ctaLabel;
      },

      get savingsLabel() {
        return this.message('savings', { percentage: this.discountPercentage });
      },

      toggleProduct(index) {
        this.expandedProductIndex = this.expandedProductIndex === index ? null : index;
      },

      isVariantAvailable(index) {
        const product = this.products[index];
        return Boolean(product && product.selectedVariant && product.selectedVariant.available);
      },

      get bundleProductCount() {
        return this.products.length;
      },

      get hasBundleProducts() {
        return this.bundleProductCount > 0;
      },

      get allSelectedAvailable() {
        return this.hasBundleProducts && this.products.every((product, index) => this.isVariantAvailable(index));
      },

      get allSelectedAllowOne() {
        return this.hasBundleProducts && this.products.every(product => {
          const rule = product.selectedVariant?.quantity_rule || {};
          return Number(rule.min ?? 1) <= 1 && Number(rule.increment ?? 1) === 1 &&
            (rule.max == null || Number(rule.max) >= 1);
        });
      },

      get quantityRuleMessage() {
        return this.message('quantityRule');
      },

      get cartUrl() {
        return (window.Shopify?.routes?.root || '/') + 'cart';
      },

      selectVariant(productIndex, variantId) {
        if (this.loading) return;
        const product = this.products[productIndex];
        if (!product || !product.variants) return;

        const variant = product.variants.find((v) => String(v.id) === String(variantId));
        if (!variant) return;
        const previousSchedule = this.selectedSchedule;

        product.selectedVariant = variant;
        product.selectedVariantId = variant.id;
        product.price = variant.price;
        product.compareAtPrice = variant.compare_at_price || 0;
        product.featuredImage = variant.featured_image ? variant.featured_image.src : product.defaultImage;

        const schedules = this.subscriptionSchedules;
        if (!schedules.some(schedule => schedule.key === this.selectedScheduleKey)) {
          const signature = previousSchedule?.allocations[0].signature;
          const equivalent = signature ? schedules.filter(schedule => schedule.allocations[0].signature === signature) : [];
          this.selectedScheduleKey = equivalent.length === 1 ? equivalent[0].key : '';
        }
        this.statusMessage = '';
        if (this.purchaseOptions && this.requiresSubscription) this.purchaseType = 'subscription';

        this.errorMessage = variant.available
          ? ''
          : this.message('soldOutVariant');
      },

      get canBuyOnce() {
        return !this.cartRecovery && this.allSelectedAvailable && this.allSelectedAllowOne && !this.requiresSubscription;
      },

      get requiresSubscription() {
        return this.products.some(product => product.selectedVariant?.requires_selling_plan);
      },

      get canPurchase() {
        return !this.cartRecovery && (this.purchaseType === 'subscription' ? this.canSubscribe : this.canBuyOnce);
      },

      selectPurchaseType(type) {
        if (this.loading || !['one-time', 'subscription'].includes(type)) return;
        if (type === 'subscription' && !this.purchaseOptions) return;
        if (type === 'one-time' ? !this.canBuyOnce : !this.allSelectedAvailable || !this.allSelectedAllowOne || !this.subscriptionSchedules.length) return;
        this.purchaseType = type;
        this.errorMessage = '';
        this.statusMessage = '';
      },

      get formattedPurchaseTotal() {
        return this.purchaseType === 'subscription' ? this.formattedSubscriptionTotal : this.formattedDiscountedSubtotal;
      },

      get purchasePriceLabel() {
        return this.purchaseType === 'subscription' ? this.message('initialTotal') : this.countMessage('bundleTotal', this.bundleProductCount);
      },

      get purchaseOptionsMessage() {
        if (!this.hasBundleProducts) return this.message('noProducts');
        if (!this.allSelectedAvailable) return this.message('stockRequired');
        if (!this.allSelectedAllowOne) return this.quantityRuleMessage;
        if (this.requiresSubscription) return this.purchaseOptions ? this.message('subscriptionRequired') : this.message('subscriptionRequiredDisabled');
        if (!this.purchaseOptions) return '';
        if (!this.subscriptionSchedules.length) return this.message('noSharedSchedule');
        return '';
      },

      get showPurchaseOriginalPrice() {
        return this.purchaseType === 'one-time' && this.originalSubtotal > 0 && this.discountPercentage > 0;
      },

      get showPurchaseSavings() {
        return this.purchaseType === 'one-time' && this.totalSavings > 0 && this.discountPercentage > 0;
      },

      get includedProductsLabel() {
        return this.bundleProductCount === 2 ? this.message('includedBoth') : this.countMessage('includedProducts', this.bundleProductCount);
      },

      get subscriptionSchedules() {
        if (!this.purchaseOptions || !this.hasBundleProducts) return [];
        const plans = product => (product.selectedVariant?.plans || []).filter(plan => plan.recurring && plan.quantity === 1);
        const schedules = [];
        for (const seed of plans(this.products[0])) {
          const allocations = this.products.map(product => {
            const available = plans(product);
            const exact = available.filter(plan => String(plan.id) === String(seed.id));
            if (exact.length === 1) return exact[0];
            const matching = seed.signature ? available.filter(plan => plan.signature === seed.signature) : [];
            return matching.length === 1 ? matching[0] : null;
          });
          if (allocations.some(plan => !plan)) continue;
          // Allocation IDs distinguish genuinely different offers with the same display label.
          const key = allocations.map(plan => String(plan.id)).join(':');
          if (!schedules.some(schedule => schedule.key === key)) {
            schedules.push({ key, label: seed.name, allocations });
          }
        }
        return schedules;
      },

      get selectedSchedule() {
        return this.subscriptionSchedules.find(schedule => schedule.key === this.selectedScheduleKey) || null;
      },

      get canSubscribe() {
        return !this.cartRecovery && this.purchaseOptions && this.allSelectedAvailable && this.allSelectedAllowOne && !!this.selectedSchedule;
      },

      get subscriptionMessage() {
        if (!this.purchaseOptions) return this.message('subscriptionsDisabled');
        if (!this.hasBundleProducts) return this.message('noProducts');
        if (!this.allSelectedAvailable) return this.message('stockSubscribe');
        if (!this.allSelectedAllowOne) return this.quantityRuleMessage;
        if (!this.subscriptionSchedules.length) return this.message('noSharedSchedule');
        if (!this.selectedSchedule) return this.message('chooseSchedule');
        return this.message('scheduleReady');
      },

      get formattedSubscriptionTotal() {
        return this.selectedSchedule ? this.formatMoney(this.selectedSchedule.allocations.reduce((sum, plan) => sum + Number(plan.price), 0)) : '—';
      },

      get subscriptionPriceDetails() {
        if (!this.selectedSchedule) return [];
        return this.selectedSchedule.allocations.flatMap((plan, index) => {
          const details = [];
          if (plan.checkoutPrice !== plan.price) details.push(this.message('chargedToday', { price: this.formatMoney(plan.checkoutPrice) }));
          if (plan.perDeliveryPrice !== plan.price) details.push(this.message('perDelivery', { price: this.formatMoney(plan.perDeliveryPrice) }));
          if (plan.initialOrderCount > 0 && plan.laterPrice !== null && plan.laterPrice !== plan.price) {
            details.push(this.countMessage('introductory', plan.initialOrderCount, { price: this.formatMoney(plan.price), later_price: this.formatMoney(plan.laterPrice) }));
          }
          return details.length ? [`${this.products[index].title}: ${details.join(' ')}`] : [];
        });
      },

      formatMoney(cents) {
        let formatted;
        if (window.Alpine && Alpine.store('xHelper') && typeof Alpine.store('xHelper').formatMoney === 'function') {
          formatted = Alpine.store('xHelper').formatMoney(cents, this.moneyFormat);
        } else {
          const formats = {
            amount: [2, ',', '.'], amount_no_decimals: [0, ',', '.'],
            amount_with_comma_separator: [2, '.', ','], amount_no_decimals_with_comma_separator: [0, '.', ','],
            amount_with_apostrophe_separator: [2, "'", '.'],
            amount_with_space_separator: [2, ' ', ','], amount_no_decimals_with_space_separator: [0, ' ', ','],
            amount_with_period_and_space_separator: [2, ' ', '.']
          };
          formatted = this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (_placeholder, token) => {
            const [precision, thousands, decimal] = formats[token] || formats.amount;
            const parts = (Number(cents || 0) / 100).toFixed(precision).split('.');
            return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (parts[1] ? decimal + parts[1] : '');
          });
        }
        // Money formats may contain HTML and entities. Parse in an inert fragment;
        // prices remain plain text through Alpine's x-text bindings.
        if (!/[<&]/.test(formatted)) return formatted;
        const template = document.createElement('template');
        template.innerHTML = formatted;
        return template.content.textContent || '';
      },

      get originalSubtotal() {
        return this.products.reduce((sum, product) => {
          return sum + (product && product.selectedVariant ? Number(product.selectedVariant.price) : 0);
        }, 0);
      },

      get discountedSubtotal() {
        if (!this.discountPercentage) return this.originalSubtotal;

        const discount = Math.round(this.originalSubtotal * (this.discountPercentage / 100));
        return Math.max(0, this.originalSubtotal - discount);
      },

      get totalSavings() {
        return Math.max(0, this.originalSubtotal - this.discountedSubtotal);
      },

      get formattedOriginalSubtotal() {
        return this.formatMoney(this.originalSubtotal);
      },

      get formattedDiscountedSubtotal() {
        return this.formatMoney(this.discountedSubtotal);
      },

      get formattedSavings() {
        return this.formatMoney(this.totalSavings);
      },

      makeBundleProperties() {
        return {
          _bundle_id: `${this.sectionId}-${Date.now()}`,
          _bundle_title: this.bundleTitle,
          _bundle_product_count: String(this.bundleProductCount),
          _bundle_discount_display: this.discountPercentage ? `${this.discountPercentage}%` : ''
        };
      },

      buildCartItems(purchaseType = 'one-time') {
        if (!this.allSelectedAllowOne) throw new Error(this.quantityRuleMessage);
        const properties = this.makeBundleProperties();
        const schedule = this.selectedSchedule;
        if (purchaseType === 'subscription' && !this.canSubscribe) throw new Error(this.message('invalidSchedule'));

        return this.products.map((product, index) => ({
          id: product.selectedVariant.id,
          quantity: 1,
          properties,
          ...(purchaseType === 'subscription' ? { selling_plan: schedule.allocations[index].id } : {})
        }));
      },

      async handleAddToCart(purchaseType = this.purchaseType) {
        if (this.loading || this.cartRecovery) return;
        if (!this.hasBundleProducts) {
          this.errorMessage = this.message('emptyBundle');
          return;
        }

        if (!this.allSelectedAvailable) {
          this.errorMessage = this.message('bundleSoldOut');
          return;
        }

        if (purchaseType === 'subscription' ? !this.canSubscribe : !this.canBuyOnce) {
          this.errorMessage = purchaseType === 'subscription' ? this.subscriptionMessage : this.purchaseOptionsMessage;
          return;
        }

        // Snapshot before waiting for another cart operation, not after acquiring the lock.
        const items = this.buildCartItems(purchaseType);
        this.loading = true;
        this.pendingPurchase = purchaseType;
        this.errorMessage = '';
        this.cartRefreshFailed = false;
        this.statusMessage = purchaseType === 'subscription' ? this.message('addingSubscription') : this.message('addingBundle');

        let releaseCart;
        let added = false;
        let cartResponseError = false;
        let postStarted = false;
        try {
          releaseCart = await window.mwCart.acquire();

          let sectionsToRender = [];
          if (window.Alpine && Alpine.store('xCartHelper') && typeof Alpine.store('xCartHelper').getSectionsToRender === 'function') {
            sectionsToRender = Alpine.store('xCartHelper').getSectionsToRender().slice(0, 5).map((section) => section.id);
          }

          const payload = {
            items
          };

          if (sectionsToRender.length) {
            payload.sections = sectionsToRender;
          }

          const requestBody = JSON.stringify(payload);
          postStarted = true;
          const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: requestBody
          });

          // A successful POST remains successful even if JSON, sections or app UI fail.
          added = response.ok;
          cartResponseError = !response.ok;
          const data = await response.json();

          if (!response.ok || Number(data.status) >= 400 || data.errors) {
            added = false;
            cartResponseError = true;
            const message = data.description || data.message || this.message('addFailed');
            this.errorMessage = message;
            document.dispatchEvent(new CustomEvent('eurus:product:add-failed', { detail: { errorMessage: message } }));
            try {
              await this.refreshCart(data.sections);
            } catch (refreshError) {
              this.cartRefreshFailed = true;
              console.warn('Unable to reconcile cart after bundle rejection:', refreshError);
            }
            return;
          }

          document.dispatchEvent(new CustomEvent('eurus:product:added', { detail: { product: data.items || data } }));
          this.statusMessage = purchaseType === 'subscription' ? this.message('addedSubscription') : this.message('addedBundle');
          await this.refreshCart(data.sections);

          if (window.Alpine) {
            if (Alpine.store('xQuickView') && Alpine.store('xQuickView').show) {
              Alpine.store('xQuickView').show = false;
            }

            if (Alpine.store('xPopup') && typeof Alpine.store('xPopup').close === 'function') {
              Alpine.store('xPopup').close();
            }

            if (Alpine.store('xCartNoti') && Alpine.store('xCartNoti').enable) {
              Alpine.store('xCartNoti').setItem(data);
            } else if (Alpine.store('xMiniCart') && typeof Alpine.store('xMiniCart').openCart === 'function') {
              Alpine.store('xMiniCart').openCart();
            }
          }
          document.dispatchEvent(new CustomEvent('eurus:cart:redirect'));
        } catch (err) {
          console.error('Cart add error:', err);
          if (added || (postStarted && !cartResponseError)) {
            // A lost response cannot prove the POST failed. Never invite a duplicate addition.
            this.cartRecovery = true;
            this.statusMessage = this.message('cartRecovery');
            try { window.location.assign(this.cartUrl); }
            catch (navigationError) { console.warn('Cart recovery navigation failed:', navigationError); }
          } else {
            this.errorMessage = cartResponseError ? this.message('addFailed') : this.message('networkError');
            document.dispatchEvent(new CustomEvent('eurus:product:add-failed', { detail: { errorMessage: this.errorMessage } }));
            if (cartResponseError) {
              try { await this.refreshCart(); }
              catch (refreshError) {
                this.cartRefreshFailed = true;
                console.warn('Unable to reconcile cart after bundle rejection:', refreshError);
              }
            }
          }
        } finally {
          if (added || cartResponseError || (postStarted && this.cartRecovery)) document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
          releaseCart?.();
          this.loading = false;
          this.pendingPurchase = '';
          if (this.errorMessage) this.statusMessage = '';
        }
      },

      async refreshCart(sections) {
        const helper = window.Alpine && Alpine.store('xCartHelper');
        // Attempt both updates even if either fails, then preserve the failure for recovery.
        const results = await Promise.allSettled([
          (async () => {
            const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', { headers: { Accept: 'application/json' } });
            if (!response.ok) throw new Error(`Cart count request failed: ${response.status}`);
            const cart = await response.json();
            if (typeof cart.item_count !== 'number') throw new Error('Cart count response is incomplete.');
            if (helper) helper.currentItemCount = cart.item_count;
          })(),
          (async () => {
            if (typeof helper?.reRenderSections === 'function') await helper.reRenderSections(sections);
            else if (typeof Alpine.store('xMiniCart')?.reLoad === 'function') await Alpine.store('xMiniCart').reLoad();
          })()
        ]);
        const failure = results.find(result => result.status === 'rejected');
        if (failure) throw failure.reason;
      }
    }));
  };

  document.addEventListener('alpine:init', registerBundleProducts);
  registerBundleProducts();
})();
